import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs'
import { Product } from '../core/models/product.model';
import { HttpClient } from '@angular/common/http';
import { API_CMS_ENDPOINT, API_ENDPOINT } from '../core/DI';
import { Constants } from '../core/Constants';
import { KitComponentsDtoResponse } from '../core/models/kit-components.dto-response';
import { KitPackage } from '../core/models/kit-package';
import { PackageBuilderModel } from '../core/models/package-builder.model';
import { StaggeredPackageBuilderModel } from '../core/models/staggered-package-builder.model';
import { GenericSummaryCardDtoResponse } from 'app/core/models/generic-summary-card.dto-response';
import { ProductCardDtoResponse } from '../core/models/product-card-dto-response';

@Injectable()
export class AddToCartService {
    apiEndpoint: string;
    apiCmsEndpoint: string;
    KitNo: string;
    customParameter: {} = null;

    constructor(@Inject(API_ENDPOINT) apiEndpoint: string,
        @Inject(API_CMS_ENDPOINT) apiCmsEndpoint: string,
        private http: HttpClient) {
        this.apiEndpoint = apiEndpoint;
        this.apiCmsEndpoint = apiCmsEndpoint;
    }

    private getSelectedProductSource = new Subject<any>();
    getSelectedProduct$ = this.getSelectedProductSource.asObservable();

    private setSelectedProductSource = new Subject<any>();
    setSelectedProduct$ = this.setSelectedProductSource.asObservable();

    private productSelectedSource = new Subject<any>();
    productSelected$ = this.productSelectedSource.asObservable();

    private selectAllProductSource = new Subject<boolean>();
    selectAllProduct$ = this.selectAllProductSource.asObservable();

    selectAllProduct(isChecked) {
        this.selectAllProductSource.next(isChecked);
    }

    productSelected(selectedProduct) {
        this.productSelectedSource.next(selectedProduct);
    }

    getSelectedProducts() {
        this.getSelectedProductSource.next();
    }

    setSelectedProduct(selectedProducts) {
        this.setSelectedProductSource.next(selectedProducts)
    }

    postProductPairToCart(frontId: string, frontQty: number, frontWarningNotes: string[], rearId: string, rearQty: number, rearWarningNotes: string[], pairNo: string, productType: string): Promise<any> {
        const api = `${this.apiCmsEndpoint}/cart/AddProduct`;

        const frontProduct: Product = new Product({
            id: frontId,
            quantity: frontQty,
            warningNotes: frontWarningNotes,
            type: Constants.PRODUCT_TYPE_TIRE,
            pairNo: pairNo
        });

        const rearProduct: Product = new Product({
            id: rearId,
            quantity: rearQty,
            warningNotes: rearWarningNotes,
            type: Constants.PRODUCT_TYPE_TIRE,
            pairNo: pairNo
        });

        return this.http.post(api, frontProduct).toPromise().then(() => {
            return this.http.post(api, rearProduct).toPromise();
        });
    }

    postProductToCart(id: string, qty: number, warningNotes: string[], pairNo: string, productType: string): Promise<any> {
        let api = `${this.apiCmsEndpoint}/cart/AddProduct`;

        const product: Product = new Product();
        product.id = id;
        product.quantity = qty;
        product.warningNotes = warningNotes;
        product.type = productType;

        // WIP - VOLUME DISCOUNT
        if (this.customParameter !== null && this.customParameter['volumeDiscount'] !== null) {
            product.volumeDiscount = this.customParameter['volumeDiscount'];
            product.isVolumeDiscountEligible = true;
        }

        // WIP - SEASONAL CARE PLAN
        if (this.customParameter !== null && this.customParameter['scpReplaceCartContent'] !== null) {
            const _scpReplaceCartContent = this.customParameter['scpReplaceCartContent'];
            if (_scpReplaceCartContent)
                api = `${this.apiCmsEndpoint}/cart/AddProduct?scpReplaceCartContent=true`;
        }

        return this.http.post(api, product).toPromise();
    }

    postOnSaleProductToCart(card: GenericSummaryCardDtoResponse, quantity: number) {
        let api = `${this.apiCmsEndpoint}/cart/AddProduct`;
        var customPriceAPI = `${this.apiCmsEndpoint}/cart/AddProduct?isCustomPrice=1`;

        const onSaleProducts = this.getCalculatedOnSaleProducts(card.frontProduct, quantity, card.frontProduct.warningNotes, card.pairNo, card.productTypeWarning, card.productBrand);
        const onSaleProduct = onSaleProducts.onSaleProduct;
        const onSaleProductAtDealerCost = onSaleProducts.onSaleProductAtDealerCost;

        let arr = [];
        if (onSaleProduct === null)
            arr.push({ api: customPriceAPI, product: onSaleProductAtDealerCost });
        else {
            arr.push({ api: api, product: onSaleProduct });

            if (onSaleProductAtDealerCost !== null)
                arr.push({ api: customPriceAPI, product: onSaleProductAtDealerCost });
        }

        this.sequentialPost(arr)
            .then(() => {
                window.location.href = Constants.SHOPPING_CART_HREF;
            }).catch((rearErr: any) => {
                //console.log(rearErr);
                window.location.href = Constants.SHOPPING_CART_HREF;
            });
    }

    postOnSaleProductPairToCart(card: GenericSummaryCardDtoResponse, frontQuantity: number, rearQuantity: number) {
        const api = `${this.apiCmsEndpoint}/cart/AddProduct`;
        var customPriceAPI = `${this.apiCmsEndpoint}/cart/AddProduct?isCustomPrice=1`;

        const frontProducts = this.getCalculatedOnSaleProducts(card.frontProduct, frontQuantity, card.frontProduct.warningNotes, card.pairNo, Constants.PRODUCT_TYPE_TIRE);
        const frontOnSaleProduct = frontProducts.onSaleProduct;
        const frontOnSaleProductAtDealerCost = frontProducts.onSaleProductAtDealerCost;

        const rearProducts = this.getCalculatedOnSaleProducts(card.rearProduct, rearQuantity, card.rearProduct.warningNotes, card.pairNo, Constants.PRODUCT_TYPE_TIRE);
        const rearOnSaleProduct = rearProducts.onSaleProduct;
        const rearOnSaleProductAtDealerCost = rearProducts.onSaleProductAtDealerCost;

        if (frontOnSaleProductAtDealerCost !== null && rearOnSaleProductAtDealerCost === null)
            frontOnSaleProductAtDealerCost.pairNo = null;

        if (rearOnSaleProductAtDealerCost !== null && frontOnSaleProductAtDealerCost === null)
            rearOnSaleProductAtDealerCost.pairNo = null;

        var arr = [];
        arr.push({ api: api, product: frontOnSaleProduct });
        arr.push({ api: api, product: rearOnSaleProduct });

        if (frontOnSaleProductAtDealerCost !== null)
            arr.push({ api: customPriceAPI, product: frontOnSaleProductAtDealerCost });

        if (rearOnSaleProductAtDealerCost !== null)
            arr.push({ api: customPriceAPI, product: rearOnSaleProductAtDealerCost });

        this.sequentialPost(arr)
            .then(() => {
                window.location.href = Constants.SHOPPING_CART_HREF;
            }).catch((rearErr: any) => {
                window.location.href = Constants.SHOPPING_CART_HREF;
            });
    }

    postKitToCart(id: string, qty: number): Promise<any> {
        this.KitNo = id;

        const api = `${this.apiEndpoint}/items/kitcomponents/?id=${encodeURIComponent(id)}`;
        return this.http.get<KitComponentsDtoResponse>(api).toPromise().then(responseProduct => {
            if (responseProduct != null) {
                const api = `${this.apiCmsEndpoint}/cart/AddKit`;
                const kitPackage: KitPackage = new KitPackage();

                kitPackage.kitID = this.KitNo;
                kitPackage.kit = this.createProduct(id, qty, Constants.PRODUCT_TYPE_KIT, null, "", 1);
                kitPackage.package = new StaggeredPackageBuilderModel();
                kitPackage.package.front = new PackageBuilderModel();
                kitPackage.package.rear = new PackageBuilderModel();

                for (let num = 0; num < responseProduct.cards.length; num++) {
                    if (responseProduct.cards[num].id != null) {

                        switch (responseProduct.cards[num].itemCategoryId) {
                            case 1://Accessories
                                kitPackage.package.front.hardwares.push(this.createProduct(responseProduct.cards[num].id, responseProduct.cards[num].kitQty * qty, Constants.PRODUCT_TYPE_HARDWARE, null, "", num + 2));
                                break;
                            case 2://Wheels
                                kitPackage.package.front.wheel = this.createProduct(responseProduct.cards[num].id, responseProduct.cards[num].kitQty * qty, Constants.PRODUCT_TYPE_WHEEL, null, "", num + 2);
                                break;
                            case 3://Tires
                                kitPackage.package.front.tire = this.createProduct(responseProduct.cards[num].id, responseProduct.cards[num].kitQty * qty, Constants.PRODUCT_TYPE_TIRE, null, "", num + 2);
                                break;
                            case 4://Kits
                            case 5://Services
                            case 6://Other
                                continue;
                            case 7://Consumables
                                kitPackage.package.front.labours.push(this.createProduct(responseProduct.cards[num].id, responseProduct.cards[num].kitQty * qty, Constants.PRODUCT_TYPE_LABOUR, null, "", num + 2));
                                break;
                            default:
                                break;
                        }
                    }
                }
                return this.http.post(api, kitPackage).toPromise();
            }
        }
        );
    }

    private createProduct(id: string, qty: number, productType: string, warningNotes: string[], pairNo: string, sequence: number): Product {
        return new Product({
            id: id,
            warningNotes: warningNotes,
            quantity: qty,
            type: productType, //Constants.PRODUCT_TYPE_WHEEL,
            pairNo: pairNo,
            sequence: sequence
        });
    }

    goToCart() {
        window.location.href = Constants.SHOPPING_CART_HREF;
    }

    sequentialPost(arr) {
        return arr.reduce((promise, item) => {
            return promise
                .then(() => {
                    return this.http.post(item.api, item.product).toPromise();
                })
        }, Promise.resolve())
    }

    private getCalculatedOnSaleProducts(summaryProduct: ProductCardDtoResponse, quantity: number, warningNotes: string[], pairNo: string, productType: string, productBrand: string = ''): any {
        let result = {
            onSaleProduct: null,
            onSaleProductAtDealerCost: null
        };
        const _isWheel = productType === 'Wheel';

        let actualOnHand = summaryProduct.inventory.onHandQtyMtl;

        if (summaryProduct.defaultWarehouse === 'CAL') {
          actualOnHand = summaryProduct.inventory.onHandQtyCal;
        }

        if (summaryProduct.onHandDetails && summaryProduct.onHandDetails.length > 0) {
            actualOnHand = 0;

            const mtlStock = summaryProduct.onHandDetails.find(d => d.label === 'FC.Inventory.StockAvailable.MTL');
            if (mtlStock)
                actualOnHand += mtlStock.qty

            if (_isWheel) {
                const mtlAlternateStock = summaryProduct.onHandDetails.find(d => d.label === 'FC.Inventory.AlternateWarehouse.MTL');
                if (mtlAlternateStock && summaryProduct.defaultWarehouse === 'MTL')
                    actualOnHand += mtlAlternateStock.qty;
            }

            if (!mtlStock && summaryProduct.inventory)
                actualOnHand = summaryProduct.inventory.onHandQtyMtl;

            if (summaryProduct.defaultWarehouse === 'CAL') {
              actualOnHand = 0;

                const calStock = summaryProduct.onHandDetails.find(d => d.label === 'FC.Inventory.StockAvailable.CAL');
                if (calStock)
                    actualOnHand += calStock.qty

                if (_isWheel) {
                    const calAlternateStock = summaryProduct.onHandDetails.find(d => d.label === 'FC.Inventory.AlternateWarehouse.CAL');
                    if (calAlternateStock)
                        actualOnHand += calAlternateStock.qty;
                }

                if (!calStock && summaryProduct.inventory)
                    actualOnHand = summaryProduct.inventory.onHandQtyCal;
            }
        }

        //Items on special are transferred at regular price
        //const _isBraelin = productBrand.toUpperCase().includes('BRAELIN');
        //if (_isBraelin)
        //    actualOnHand = summaryProduct.inventory.onHandQtyMtl + summaryProduct.inventory.onHandQtyCal;

        let regularQty = 0;
        let onSaleQty = quantity;

        if (quantity > actualOnHand) {
            onSaleQty = actualOnHand;
            regularQty = quantity - actualOnHand;
        }

        if (onSaleQty > 0) {
            result.onSaleProduct = new Product({
                id: summaryProduct.longSKU,
                quantity: onSaleQty,
                warningNotes: warningNotes,
                type: productType,
                pairNo: pairNo
            });
        }

        if (regularQty >= 1) {
            result.onSaleProductAtDealerCost = new Product({
                id: summaryProduct.longSKU,
                quantity: regularQty,
                warningNotes: warningNotes,
                type: productType,
                pairNo: pairNo
            });
            result.onSaleProductAtDealerCost['UnitPrice'] = summaryProduct.dealerCost;
            result.onSaleProductAtDealerCost['IsOnSpecialAtDealerCost'] = true;
        }

        return result;
    }

    postProductsToCart(products: Product[]) {

        let arr = [];
        products.forEach(p => {
            arr.push({ api: `${this.apiCmsEndpoint}/cart/AddProduct`, product: p });
        });

        this.sequentialPost(arr)
            .then(() => {
                window.location.href = Constants.SHOPPING_CART_HREF;
            }).catch((rearErr: any) => {
                //console.log(rearErr);
                window.location.href = Constants.SHOPPING_CART_HREF;
            });
    }
}
